import React, { memo, useCallback, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { useQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';

import { PENDING_STOCK_SOURCE_TYPE, STATUS } from '../../../../common/constants';
import { GET_PENDING_STOCK_LIST } from '../../../../common/graphql/pendingStock';
import { useUserContext } from '../../../../common/utils/context';
import { MY_DAY_STEP_STATUS } from '../../../../common/utils/myDay/driverDaySteps';
import { ActionDock, EmptyState, Screen, SectionTitle, TickRow } from '../../../design/components';
import { useTheme } from '../../../theme';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { useDriverDay } from '../DriverDayProvider';
import DriverTrainingBanner from '../DriverTrainingBanner';
import { RUN_LOAD_ERROR } from '../driverRoutes';
import useDriverDayActions from '../useDriverDayActions';
import StepCount from './StepCount';
import StepNote from './StepNote';
import { countLabel } from './stopLabels';
import useEndOfDaySteps from './useEndOfDaySteps';

const STEP_KEY = 'returnStock';

const ReturnStockRow = memo(({ item, checked, disabled, onToggle }) => {
	const handleToggle = useCallback(nextChecked => onToggle(item.id, nextChecked), [item.id, onToggle]);

	return (
		<TickRow
			title={item.product?.name || 'Unknown product'}
			value={`x${item.quantity}`}
			checked={checked}
			disabled={disabled}
			onToggle={handleToggle}
		/>
	);
});

/**
 * Return stock (End of day): today's pending driver returns (the list the
 * classic board shows in board/ReturnStockList), one tick per product, then
 * the existing "Stock returned" confirmation. The list is informative: a
 * failed or empty list never blocks the confirmation.
 */
const ReturnStockView = () => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const { user } = useUserContext();
	const day = useDriverDay();
	const goBack = useCallback(() => navigation.goBack(), [navigation]);
	const { confirmStepByKey, isConfirmingStep } = useDriverDayActions(day, { onStepConfirmed: goBack });
	const { steps, getStepAfter, getStepPosition } = useEndOfDaySteps(day);
	const [tickedById, setTickedById] = useState({});

	const step = steps.find(item => item.key === STEP_KEY) || null;
	const isDone = step?.status === MY_DAY_STEP_STATUS.DONE;
	const isBlocked = step?.status === MY_DAY_STEP_STATUS.BLOCKED;
	const driverId = day.isTraining ? day.serviceRun?.driver?.id : user?.id;

	// Same query as board/ReturnStockList (left untouched for the classic board).
	const { data, loading, error, refetch } = useQuery(GET_PENDING_STOCK_LIST, {
		variables: {
			page: 0,
			perPage: 200,
			sortBy: 'pickOrder',
			sortDirection: -1,
			filter: {
				status: STATUS.PENDING,
				sourceType: PENDING_STOCK_SOURCE_TYPE.REMOVED,
				driver: driverId,
				runDate: day.todayDateKey,
			},
		},
		skip: !driverId || !day.todayDateKey,
		fetchPolicy: day.isTraining ? 'no-cache' : 'cache-and-network',
		errorPolicy: 'all',
	});
	const items = data?.getPendingStocks?.items || [];
	const tickedCount = isDone ? items.length : items.filter(item => tickedById[item.id]).length;
	const unitCount = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

	const toggle = useCallback((itemId, nextChecked) => {
		setTickedById(previous => ({ ...previous, [itemId]: nextChecked }));
	}, []);
	const confirm = useCallback(() => confirmStepByKey(STEP_KEY), [confirmStepByKey]);

	const renderDock = () => {
		if (!step || isBlocked) {
			return null;
		}
		if (isDone) {
			return <ActionDock action={{ label: 'Back to end of day', onPress: goBack }} />;
		}

		const stepAfter = getStepAfter(STEP_KEY);
		let hint = stepAfter ? `After this: ${stepAfter.label}` : undefined;
		if (items.length && tickedCount < items.length) {
			hint = `${tickedCount} of ${countLabel(items.length, 'product', 'products')} returned`;
		}
		else if (!day.isOnline && !day.isTraining) {
			hint = 'You are offline. This is saved on the phone and syncs later.';
		}

		return (
			<ActionDock
				hint={hint}
				action={{ label: 'Stock returned', onPress: confirm, loading: isConfirmingStep }}
			/>
		);
	};

	const renderList = () => {
		if (loading && !items.length) {
			return <ActivityIndicator color={colors.accent} style={styles.spinner} />;
		}
		if (error && !items.length) {
			return (
				<EmptyState
					tone="error"
					title="Stock list not loaded"
					message="Return your stock as usual and confirm below."
					actionLabel="Try again"
					onAction={() => refetch()}
				/>
			);
		}
		if (!items.length) {
			return (
				<EmptyState
					title="No returns recorded today"
					message="If you have stock in the van, return it as usual and confirm below."
				/>
			);
		}

		return (
			<>
				<SectionTitle title="Coming back on the van" meta={countLabel(unitCount, 'item', 'items')} />
				<View style={styles.list}>
					{items.map(item => (
						<ReturnStockRow
							key={item.id}
							item={item}
							checked={isDone || Boolean(tickedById[item.id])}
							disabled={isDone || isBlocked}
							onToggle={toggle}
						/>
					))}
				</View>
			</>
		);
	};

	return (
		<Screen
			backLabel="End of day"
			headerRight={<StepCount position={getStepPosition(STEP_KEY)} />}
			heading="Return stock"
			subheading="In-date stock goes back to the warehouse floor. Expired stock goes in the designated tubs."
			isLoading={day.isLoading}
			error={day.loadError ? RUN_LOAD_ERROR : undefined}
			onRetry={day.refresh}
			banner={<DriverTrainingBanner />}
			dock={renderDock()}
		>
			{isDone && <StepNote tone="success" text={step.description} />}
			{isBlocked && <StepNote tone="warning" text={step.description} />}
			{step ? renderList() : <EmptyState title="No run today" message="See your manager if you are expecting a run." />}
		</Screen>
	);
};

const createStyles = ({ ui: { space } }) => ({
	list: {
		gap: space.sm,
	},
	spinner: {
		paddingVertical: space.xl,
	},
});

export default ReturnStockView;
